<template>

	<Modal v-if="showMissingPupilsNotification" :closeOnClickOutside="true" @requestUnmount="showMissingPupilsNotification = false">
		<template #header>&nbsp;</template>

		<template #default="modalActions">
			<p class="py-8 text-center">
				{{  $t('We cannot find any pupils to evaluate in this classroom') }}
			</p>
		</template>
	</Modal>

	<div class="overflow-x-auto h-full flex-1 min-h-0 relative pr-3">

		<div v-if="loading" class="absolute h-52 z-20 w-full flex justify-center">
			<div class="mt-16 w-16 h-16 bg-white bg-opacity-90 rounded-xl flex justify-center items-center">
				<span class="loading loading-spinner loading-md"></span>
			</div>
		</div>

		<template v-if="!loading">
			<!-- One table for all goal structures, so the pupil header, the evaluations
			     toggle and the column grid exist only once and stay aligned throughout -->
			<table v-if="hasAnyGoals" class="h-1 border-collapse goals-pupils-matrix" :class="[!showPupilsAndEvaluations? 'w-full max-w-full' : '']">
				<thead class="sticky top-0 z-10">
					<tr class="bg-base">
						<th class="font-normal text-left sticky left-0 right-0 z-10 bg-base/95" :class="[showPupilsAndEvaluations? 'w-96' : 'max-w-lg']"><!-- z-value as a consequence of rotate-180 transformation -->

							<div class="w-full h-full flex items-start relative border-b-2" :class="[onlyGoals ? '' : 'border-r-4']">
								<slot name="goalColumnHead"></slot>

								<!-- Bulk evaluation lives in this sticky corner so it stays in view while
								     scrolling both ways. The pill switches between single-cell editing and
								     group selection; the block below it is a hint or the selection summary -->
								<div v-if="bulkAvailable" data-test="bulk-corner" class="flex-1 min-w-0 flex flex-col px-4 py-3" :class="[onlyGoals? 'items-end' : 'items-center text-center gap-2']">
									<div v-if="!simpleBulkToggle" class="text-sm">{{ onlyGoals ? $t('Evaluate goals') : $t('Evaluate pupils') }}...</div>

									<!-- simpleBulkToggle: a single switch, used only where PupilGoalEvaluation embeds this matrix -->
									<div v-if="simpleBulkToggle" data-test="bulk-mode-toggle" class="flex items-center gap-2">
										<label for="bulkModeSwitch" class="text-sm hover:cursor-pointer">{{ $t('Evaluate multiple goals simultaneously') }}</label>
										<SwitchRoot
											:modelValue="bulkMode"
											@update:modelValue="$event ? enterBulkMode() : exitBulkMode()"
											id="bulkModeSwitch"
											class="w-[32px] h-[20px] shadow-sm flex data-[state=unchecked]:bg-gray-300 data-[state=checked]:bg-gray-800 border border-gray-300 data-[state=checked]:border-stone-700 rounded-full relative transition-[background]"
										>
											<SwitchThumb class="w-3.5 h-3.5 my-auto bg-white text-xs flex items-center justify-center shadow-xl rounded-full transition-transform translate-x-0.5 will-change-transform data-[state=checked]:translate-x-full" />
										</SwitchRoot>
									</div>

									<div v-else class="inline-flex rounded-full border border-base-200 p-0.5 text-sm">
										<button
											type="button"
											data-test="bulk-mode-individually"
											class="rounded-full px-3 py-1 transition-colors"
											:class="[bulkMode ? 'text-base-content/60 hover:text-base-content' : 'font-semibold is-active bg-accent/40 text-accent-content']"
											@click="exitBulkMode"
										>{{ $t('individually') }}</button>
										<button
											type="button"
											data-test="bulk-mode-group"
											class="rounded-full px-3 py-1 transition-colors"
											:class="[bulkMode ? 'is-active bg-accent/40 text-accent-content font-semibold' : 'text-base-content/60 hover:text-base-content']"
											@click="enterBulkMode"
										>{{ $t('in group') }}</button>
									</div>

									<div class="py-4 rounded-lg">
										<p v-if="!bulkMode" data-test="bulk-hint" class="text-sm text-base-content/50">
											{{ onlyGoals ? '' : $t('Push the dots to evaluate individual pupils') }}
										</p>
										<p v-else-if="selectionCount === 0" data-test="bulk-hint" class="text-sm text-base-content/50">
											{{ onlyGoals ? $t('Tick the goals you want to evaluate simultaneously') : $t('Push the dots you want to evaluate in group. Tick a goal to select all pupils.') }}
										</p>
										<template v-else>
											<div data-test="bulk-count" class="flex items-center gap-3">
												<div>
													<button type="button" data-test="bulk-evaluate" class="btn btn-primary" :class="[onlyGoals? 'btn-sm' : '']" @click="emitBulkEvaluate">
														{{ $t('Evaluate') }}
													</button>
												</div>
												<div class="flex flex-col gap-1 items-end">
													<span class="badge" data-test="bulk-count-goals">{{ selectionSummary.goals }}</span>
													<span v-if="!onlyGoals" class="badge" data-test="bulk-count-pupils">{{ selectionSummary.pupils }}</span>
												</div>
												<div class="flex flex-col text-sm gap-1 items-start">
													<div>
														{{ $tc('Goal', selectionSummary.goals) }}
													</div>
													<div v-if="!onlyGoals">
														{{ $tc('Pupil', selectionSummary.pupils) }}
													</div>
												</div>
											</div>
										</template>
									</div>
								</div>
							</div>

							<!-- collapse evaluations button -->
							<template v-if="showPupilsAndEvaluations">
								<div class="absolute top-8 -right-3 z-10 rounded-xl bg-white bg-opacity-60">
									<button type="button" class="group flex items-center gap-4" @click="closePupilsAndEvaluations">
										<div class="bg-white shadow-lg h-12 w-7 shrink-0 rounded-xl p-1">
											<div class="w-full h-full flex items-center justify-center rounded-xl group-hover:bg-gray-100 transition-colors">
												<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" class="w-3 h-3 transform rotate-180"><path d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 278.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z"/></svg>
											</div>
										</div>
									</button>
								</div>
							</template>

						</th>
						<template v-if="showPupilsAndEvaluations">
							<th v-for="(pupil, index) in pupils" class="relative p-0">
								<div class="border-b border-r w-full h-full">
									<!-- wrap in div for writing mode to work in Safari -->
									<!-- w-10 (not min-w) so the columns keep the same width as the headerless tables below -->
									<div class="h-44 w-10 rotate-180 truncate py-4 px-2.5 font-normal text-sm text-left" :title="pupil.getFullName()" style="writing-mode: vertical-lr;">{{ pupil.getFullName() }}</div>
									<PupilSymbol :pupil="pupil" class="w-8 h-8 place-self-center mb-1" />
								</div>
							</th>
						</template>
						<!-- collapsed skeleton preview + open button -->
						<template v-else-if="!onlyGoals">
							<th class="text-left border-r relative">
								<div class="h-full w-10 py-4 px-2.5 flex items-end">
									<div class="h-44 w-5 rounded bg-gray-100"></div>
								</div>
								<!-- open evaluations button -->
								<div class="absolute top-8 -left-4 z-10 rounded-xl bg-white bg-opacity-60">
									<button type="button" class="group flex items-center gap-4" @click="pupils.length > 0 ? openPupilsAndEvaluations() : showMissingPupilsNotification = true">
										<div class="bg-primary shadow-lg h-12 w-7 shrink-0 flex items-center justify-center rounded-xl group-hover:bg-[#fbbd23] transition-colors">
											<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" class="w-3 h-3"><path d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 278.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z"/></svg>
										</div>
										<div class="leading-4 text-left font-normal text-sm py-1 border-b-2 border-transparent group-hover:border-primary">
											{{ $t('Show evaluations') }}
										</div>
									</button>
								</div>
							</th>
							<th class="text-left border-r relative">
								<div class="h-full w-10 py-4 px-2.5 flex items-end">
									<div class="h-24 w-5 rounded bg-gray-100"></div>
								</div>
							</th>
							<th class="text-left border-r relative">
								<div class="h-full w-10 py-4 px-2.5 flex items-end">
									<div class="h-36 w-5 rounded bg-gray-100"></div>
								</div>
							</th>
						</template>
					</tr>
				</thead>
				<tbody>
					<template v-for="(goalStructure, index) in goalStructures" :key="goalStructure.sourceId">
						<th v-if="goalStructures.length > 1" colspan="100" class="text-left p-0">
							<div class="text-accent-content text-lg font-semibold py-1 px-2 bg-accent/15 w-full"
								:class="[index > 0 ? 'mt-6' : '']"
							>
								{{ goalStructure.name }}
							</div>
						</th>

						<template v-for="group in goalStructure.items" :key="group.uniquePath">
							<tr class="sticky z-20 bg-base" :class="[onlyGoals? '' : 'top-52']">
								<th class="text-left left-0 right-0  goal-cell">
									<h1 :style="{ color: sectionHeaderColor(group) }">
										<span class="path" :style="{ backgroundColor: sectionHeaderColor(group) }">{{ group.path }}</span>
										{{ group.title }}
									</h1>
								</th>
								<td colspan="100" class="border-b border-r"></td>
							</tr>
							<tr
								v-for="goalSearchResult in group.goalsFlat"
								:key="goalSearchResult.goal.id"
								class="group"
								:class="[goalSearchResult.goal.partiallyPlanned ? 'opacity-50' : '']"
							>
								<th class="text-sm border-b text-left sticky left-0 font-normal bg-base/90 group-hover:bg-gray-50" :class="[showPupilsAndEvaluations? 'max-w-96' : 'max-w-lg']">
									<div class="h-full" :class="[onlyGoals? '' : ' border-r-4', showPupilsAndEvaluations? 'truncate' : '']" :title="goalSearchResult.goal.title">
										<div class="px-4 py-2 ml-4 border-l-2 flex items-start gap-3" :style="{ borderColor: sectionHeaderColor(group) }">
											<input
												v-if="bulkMode"
												type="checkbox"
												data-test="goal-toggle"
												class="checkbox checkbox-accent checkbox-sm mt-0.5 shrink-0"
												:checked="isGoalRowSelected(goalSearchResult.goal)"
												:title="$t('Select all pupils for this goal')"
												@change="toggleGoalRow(goalSearchResult.goal)"
											/>
											<div class="min-w-0 grow">
												<slot name="goal" :goal="goalSearchResult.goal" :goalSearchResult="goalSearchResult">
													<GoalTitle :goal="goalSearchResult.goal" :goalSearchResult="goalSearchResult" :showPath="true" />
												</slot>
											</div>
										</div>
								</div>
							</th>
							<td v-if="showPupilsAndEvaluations" v-for="pupil in pupils" :key="pupil.id" class="border-r border-b group-hover:bg-gray-50 text-center w-10">
								<button
									type="button"
									data-test="cell"
									class="hover:scale-125 transition-all rounded-full"
									:class="[isCellSelected(pupil, goalSearchResult.goal) ? 'is-selected ring-2 ring-offset-2 ring-primary' : '']"
									@click="onCellClick(pupil, goalSearchResult.goal)"
								>
									<div v-if="goalSearchResult.goal.hasEvaluation(pupil)" class="rounded-full w-7 h-7 grid place-content-center" :style="{ backgroundColor: goalSearchResult.goal.getEvaluation(pupil)?.evaluationLevel?.color }">
										<CheckIcon v-if="isCellSelected(pupil, goalSearchResult.goal)" class="w-4 h-4 text-white" />
										<EllipsisHorizontalIcon v-else class="w-4 h-4 invisible group-hover:visible" />
									</div>
									<div v-else class="rounded-full w-7 h-7 bg-gray-100 hover:bg-gray-400 group-hover:bg-gray-200 grid place-content-center">
										<CheckIcon v-if="isCellSelected(pupil, goalSearchResult.goal)" class="w-4 h-4" />
										<EllipsisHorizontalIcon v-else class="w-4 h-4 invisible group-hover:visible" />
									</div>
								</button>
							</td>

							<td v-else-if="showPupilsAndEvaluations && pupils.length === 0" class="border-r border-b w-full">
								<!-- No pupils. -->
							</td>

							<td v-else-if="!onlyGoals" v-for="i in 3" :key="i" class="border-r border-b w-10">
								<div class="flex justify-center items-center h-full w-full">
									<div class="rounded-full w-6 h-6 self-center bg-gray-100"></div>
								</div>
							</td>
						</tr>
					</template>
					</template>
				</tbody>
			</table>

			<EmptyState v-else-if="showEmptyState"
				:title="$t('No goals planned yet')"
				:description="$t('Once you start planning lessons with goals in your agenda, the goals will show up here.')"
				:ctaLabel="$t('Agenda')"
				:ctaCallback="() => $router.push({ name: 'classroom.agenda' })"
			>
				<template #icon>
					<CalendarDaysIcon class="w-4 h-4" />
				</template>
			</EmptyState>

			<div v-else class="flex justify-center items-center h-52">
				{{ $t('No goals found') }}
			</div>

		</template>

	</div>
</template>


<style>
.goal-cell h1 {
	@apply font-semibold text-sm uppercase px-1 py-1 mb-1 mt-4 z-10 leading-6;
}
.goal-cell .path {
	@apply inline-block mr-2 px-1.5 py-0.5 text-xs text-white rounded;
}
</style>


<script lang="ts">
import { PropType } from "vue";
import { CalendarDaysIcon, CheckIcon, ChevronLeftIcon, EllipsisHorizontalIcon } from "@heroicons/vue/24/solid";
import GoalTitle from "@/components/GoalTitle.vue";
import { mapStores } from "pinia";
import { usePupilsStore } from "@/stores/Pupils.store";
import { Pupil } from "@/models/Pupil.model";
import { Goal } from "@/models/Goal.model";
import EmptyState from "./ui/EmptyState.vue";
import PupilSymbol from "@/components/ui/symbols/PupilSymbol.vue";
import {PopulatedGoalStructureContent, PopulatedGoalStructureContentItem} from "@/utils/GoalSource";
import Modal from "@/components/ui/Modal.vue";
import { MatrixSelection } from "@/utils/MatrixSelection.util";
import type { GoalSearchResult } from "@/stores/Goals.store";
import { SwitchRoot, SwitchThumb } from 'reka-ui';

export default {

	emits: [
		'selectEvaluation',
		'bulkEvaluate',
		'selectionChange',
	],

	props: {
		goalStructures: {
			type: Array<PopulatedGoalStructureContent>,
			required: true,
		},
		showEvaluationsOnLoad: {
			type: Boolean,
			default: false,
		},
		onlyGoals: {
			type: Boolean,
			default: false,
		},

		/**
		 * Renders the bulk-mode control as a single on/off switch instead of the
		 * "individually / in group" pill. Used only where PupilGoalEvaluation embeds
		 * this matrix — everywhere else keeps the pill switcher.
		 */
		simpleBulkToggle: {
			type: Boolean,
			default: false,
		},
		loading: {
			type: Boolean,
			default: false,
		},
		showEmptyState: {
			type: Boolean,
			default: true,
		},

		useCurriculumColors: {
			type: Boolean,
			default: true,
		},

		/**
		 * In onlyGoals mode there are no pupil columns. Pass the single pupil the goals are
		 * shown for to offer bulk evaluation with that pupil as the implicit column.
		 */
		bulkPupil: {
			type: Object as PropType<Pupil | null>,
			default: null,
		},
	},

	components: {
		GoalTitle,
		EmptyState,
		ChevronLeftIcon, CalendarDaysIcon, EllipsisHorizontalIcon, CheckIcon,
		PupilSymbol,
		Modal,
		SwitchRoot, SwitchThumb,
	},

	data() {
		return {
			showPupilsAndEvaluations: false,
			showMissingPupilsNotification: false,
			bulkMode: false,
			selection: new MatrixSelection(),
			// Bumped after every selection mutation so computeds re-run (the Set inside
			// MatrixSelection is not tracked by Vue).
			selectionVersion: 0,
		}
	},

	mounted() {
		if(this.showEvaluationsOnLoad && !this.onlyGoals) {
			this.openPupilsAndEvaluations();
		}
	},

	computed: {
		...mapStores(usePupilsStore),

		hasAnyGoals(): boolean {
			return this.goalStructures.some((structure: PopulatedGoalStructureContent) => structure.items.length > 0);
		},

		// Sorted locally (rather than via pupilsStore.applyOrder()) so that mounting this
		// matrix never triggers a reload of the shared pupilsStore: that reload races with
		// callers (e.g. GoalEvaluation.vue) that load pupils themselves right before reading
		// pupilsStore.items, and can wipe the list out from under them mid-flight.
		pupils(): Pupil[] {
			return [...(this.pupilsStore.items as Pupil[])].sort((a, b) => {
				return a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName);
			});
		},

		bulkAvailable(): boolean {
			if (this.onlyGoals) {
				return this.bulkPupil !== null;
			}
			return this.showPupilsAndEvaluations && this.pupils.length > 0;
		},

		/** The pupils a bulk selection can span: the columns, or the single bulkPupil. */
		bulkPupils(): Pupil[] {
			if (this.onlyGoals) {
				return this.bulkPupil ? [this.bulkPupil] : [];
			}
			return this.pupils;
		},

		visibleGoals(): Goal[] {
			const goals: Goal[] = [];
			this.goalStructures.forEach((structure: PopulatedGoalStructureContent) => {
				structure.items.forEach((group: PopulatedGoalStructureContentItem) => {
					group.goalsFlat.forEach((result: GoalSearchResult) => goals.push(result.goal));
				});
			});
			return goals;
		},

		visibleGoalIds(): string[] {
			return this.visibleGoals.map((goal: Goal) => goal.id);
		},

		bulkPupilIds(): string[] {
			return this.bulkPupils.map((pupil: Pupil) => pupil.id);
		},

		selectedPairs(): { pupil: Pupil, goal: Goal }[] {
			// Touch the version so this recomputes after each mutation.
			void this.selectionVersion;

			const pupilsById = new Map(this.bulkPupils.map((pupil: Pupil) => [pupil.id, pupil]));
			const goalsById = new Map(this.visibleGoals.map((goal: Goal) => [goal.id, goal]));

			return this.selection.pairs()
				.map((pair) => ({ pupil: pupilsById.get(pair.pupilId), goal: goalsById.get(pair.goalId) }))
				.filter((pair): pair is { pupil: Pupil, goal: Goal } => !!pair.pupil && !!pair.goal);
		},

		/** "Goals: 2, Pupils: 3", or just the goal count when the pupil is implicit. */
		selectionSummary(): { goals: number, pupils: number } {
			const pairs = this.selectedPairs as { pupil: Pupil, goal: Goal }[];
			const goalCnt = new Set(pairs.map((pair) => pair.goal.id)).size;
			const pupilCnt = new Set(pairs.map((pair) => pair.pupil.id)).size;
			return {
				'goals': goalCnt,
				'pupils': pupilCnt,
			}
			// if (this.onlyGoals) {
			// 	return this.$tc('Goal', 2) + ': ' + goalCnt;
			// }
			// return goalCnt + ' ' + this.$tc('Goal', goalCnt) + ' / ' + pupilCnt + ' ' + this.$tc('Pupil', pupilCnt);
		},

		selectionCount(): number {
			return this.selectedPairs.length;
		},
	},

	watch: {
		selectedPairs(pairs: { pupil: Pupil, goal: Goal }[]) {
			this.$emit('selectionChange', pairs);
		},
		goalStructures() {
			this.mutateSelection(() => this.selection.pruneToGoals(this.visibleGoalIds));
		},
	},

	methods: {

		// Handle the open button in the evaluations view
		openPupilsAndEvaluations() {
			this.showPupilsAndEvaluations = true;
		},

		// Handle the close button in the evaluations view
		closePupilsAndEvaluations() {
			this.exitBulkMode();
			this.showPupilsAndEvaluations = false;
		},

		selectEvaluation(pupil: Pupil, goal: Goal) {
			this.$emit('selectEvaluation', pupil, goal);
		},

		sectionHeaderColor(group: PopulatedGoalStructureContentItem) {
			if (!this.useCurriculumColors || !group || !group.color) {
				return '#818cf8'; // Default color if not specified (indigo-400 / accent-focus)
			}
			return group.color;
		},

		mutateSelection(mutation: () => void) {
			mutation();
			this.selectionVersion++;
		},

		enterBulkMode() {
			this.bulkMode = true;
		},

		exitBulkMode() {
			this.clearSelection();
			this.bulkMode = false;
		},

		clearSelection() {
			this.mutateSelection(() => this.selection.clear());
		},

		isCellSelected(pupil: Pupil, goal: Goal): boolean {
			void this.selectionVersion;
			return this.selection.has(pupil.id, goal.id);
		},

		onCellClick(pupil: Pupil, goal: Goal) {
			if (this.bulkMode) {
				this.togglePair(pupil.id, goal.id);
			} else {
				this.selectEvaluation(pupil, goal);
			}
		},

		togglePair(pupilId: string, goalId: string) {
			this.mutateSelection(() => this.selection.toggleCell(pupilId, goalId));
		},

		isGoalRowSelected(goal: Goal): boolean {
			void this.selectionVersion;
			return this.selection.isGoalRowSelected(goal.id, this.bulkPupilIds);
		},

		toggleGoalRow(goal: Goal) {
			this.mutateSelection(() => this.selection.toggleGoalRow(goal.id, this.bulkPupilIds));
		},

		emitBulkEvaluate() {
			if (this.selectedPairs.length === 0) {
				return;
			}
			this.$emit('bulkEvaluate', this.selectedPairs, this.bulkPupils);
		},

	}
}
</script>
